// apps/client/src/title/wander.ts (title screen, 2026-10-05) — a villager strolling round the plaza behind the title: a path on the
// town's walkability grid to a spot within tuning.title.wanderRadius of the plaza, at the villager walking speed, then a pause, then
// another stroll. Not the villagers' real day (that comes from the town server's clock, game/systems/villagers.ts): just company.
import type { MapData, NavGrid, Rng, TileXY, Vec2 } from '@whiskerton/shared';
import { findPath, isWalkablePos } from '@whiskerton/shared/map/nav';   // subpath-only (not in the barrel)
import { pathLength, pointAlong } from '@whiskerton/shared/villagers/schedule';

export interface WanderOpts { radius: number; speed: number; idle: readonly [number, number] }
const TRIES = 12, MAX_NODES = 4000;

/** A random walkable tile centre within `radius` of `home`, or null after a few misses. */
function spotNear(nav: NavGrid, home: Vec2, radius: number, rng: Rng): TileXY | null {
  for (let i = 0; i < TRIES; i++) {
    const x = Math.floor(home.x + (rng.next() * 2 - 1) * radius), y = Math.floor(home.y + (rng.next() * 2 - 1) * radius);
    if (isWalkablePos(nav, { x: x + 0.5, y: y + 0.5 })) return { x, y };
  }
  return null;
}
/** `n` different walkable spots near `home` for the strollers to start from. */
export function wanderStarts(nav: NavGrid, home: Vec2, n: number, rng: Rng, radius = 6): Vec2[] {
  const out: Vec2[] = [], seen = new Set<string>();
  for (let i = 0; out.length < n && i < n * 40; i++) {
    const t = spotNear(nav, home, radius, rng);
    if (!t || seen.has(`${t.x},${t.y}`)) continue;
    seen.add(`${t.x},${t.y}`);
    out.push({ x: t.x + 0.5, y: t.y + 0.5 });
  }
  return out;
}

export class Wanderer {
  pos: Vec2;
  /** Facing (radians about +y; 0 faces +z, the villager schedule's convention). */
  facing = 0;
  walking = false;
  private path: TileXY[] | null = null;
  private along = 0;
  private length = 0;
  private wait: number;
  private readonly nav: NavGrid;
  private readonly map: MapData;
  private readonly home: Vec2;
  private readonly rng: Rng;
  private readonly opts: WanderOpts;
  constructor(nav: NavGrid, map: MapData, home: Vec2, start: Vec2, rng: Rng, opts: WanderOpts) {
    this.nav = nav; this.map = map; this.home = home; this.rng = rng; this.opts = opts;
    this.pos = { ...start };
    this.facing = rng.next() * Math.PI * 2;
    this.wait = this.pause() * rng.next();                         // strollers set off at different moments
  }
  update(dt: number): void {
    if (this.path) {
      this.along = Math.min(this.length, this.along + this.opts.speed * dt);
      const p = pointAlong(this.path, this.along);
      this.pos = p.pos;
      this.facing = p.f;
      if (this.along >= this.length) { this.path = null; this.walking = false; this.wait = this.pause(); }
      return;
    }
    this.wait -= dt;
    if (this.wait > 0) return;
    const from = { x: Math.floor(this.pos.x), y: Math.floor(this.pos.y) };
    const to = spotNear(this.nav, this.home, this.opts.radius, this.rng);
    const path = to ? findPath(this.nav, this.map, from, to, { maxNodes: MAX_NODES }) : null;
    if (!path || path.length < 2) { this.wait = this.opts.idle[0]; return; }
    // The path runs tile centre to tile centre: start it from where the stroller stands, so it never jumps to its tile's centre.
    this.path = path;
    this.along = 0;
    this.length = pathLength(path);
    this.walking = true;
    const first = pointAlong(path, 0).pos;
    if (Math.hypot(first.x - this.pos.x, first.y - this.pos.y) > 1e-6) {
      this.path = [{ x: this.pos.x - 0.5, y: this.pos.y - 0.5 }, ...path.slice(1)];
      this.length = pathLength(this.path);
    }
  }
  private pause(): number { const [a, b] = this.opts.idle; return a + (b - a) * this.rng.next(); }
}
